import { useState, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, User } from 'lucide-react';
import { calendarService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { CalendarProfessional, ServiceType } from '../types/calendar';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ProfessionalList from '../components/calendar/ProfessionalList';
import ProfessionalForm from '../components/calendar/ProfessionalForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function CalendarProfessionals() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingProfessional, setEditingProfessional] = useState<CalendarProfessional | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: professionals, isLoading } = useQuery({
    queryKey: ['calendar-professionals'],
    queryFn: () => calendarService.getProfessionals(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<CalendarProfessional>) => calendarService.createProfessional(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-professionals'] });
      messageHandler.success('Profissional criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CalendarProfessional> }) =>
      calendarService.updateProfessional(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-professionals'] });
      messageHandler.success('Profissional atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => calendarService.deleteProfessional(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-professionals'] });
      messageHandler.success('Profissional excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Get basic professional data
    const data = {
      name: formData.get('name') as string,
      specialty: formData.get('specialty') as string,
    };

    // Extract service types from form data
    const serviceTypes: ServiceType[] = [];
    const formEntries = Array.from(formData.entries());

    // Group service type entries
    const serviceTypeIndices = new Set<number>();

    // Get all indices
    formEntries.forEach(([key]) => {
      const match = key.match(/service_types\[(\d+)\]/);
      if (match) {
        serviceTypeIndices.add(parseInt(match[1]));
      }
    });

    // Create service type objects
    Array.from(serviceTypeIndices).forEach(index => {
      const id = formData.get(`service_types[${index}].id`);
      const name = formData.get(`service_types[${index}].name`) as string;
      const durationMinutes = parseInt(
        formData.get(`service_types[${index}].duration_minutes`) as string
      );

      if (name && !isNaN(durationMinutes)) {
        serviceTypes.push({
          id: id ? Number(id) : undefined,
          name,
          duration_minutes: durationMinutes,
        });
      }
    });

    const professionalData = {
      ...data,
      service_types: serviceTypes,
    };

    if (editingProfessional) {
      updateMutation.mutate({ id: editingProfessional.id, data: professionalData });
    } else {
      createMutation.mutate(professionalData);
    }
  };

  const handleDelete = (professional: CalendarProfessional) => {
    messageHandler.confirm(
      'Excluir Profissional',
      'Tem certeza que deseja excluir este profissional? Esta ação não pode ser desfeita.',
      () => deleteMutation.mutate(professional.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingProfessional(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Profissionais"
          description="Gerencie os profissionais disponíveis para agendamento."
          actions={
            <Button
              onClick={() => {
                setEditingProfessional(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Profissional
            </Button>
          }
        />

        <ProfessionalList
          professionals={professionals || []}
          onEdit={professional => {
            setEditingProfessional(professional);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingProfessional ? 'Editar Profissional' : 'Novo Profissional'}
          size="lg"
        >
          <ProfessionalForm
            professional={editingProfessional}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default CalendarProfessionals;
